import React, { useState, useRef, useEffect } from 'react';
import { useLanguage, Language } from '../../context/LanguageContext';
import { ReactComponent as FlagEN } from '../../assets/icons/flag-en.svg';
import { ReactComponent as FlagDE } from '../../assets/icons/flag-de.svg';

// 🌐 Language options with their respective flags
const LANGUAGE_OPTIONS = [
  { value: 'en', Flag: FlagEN },
  { value: 'de', Flag: FlagDE },
] as const;

function LanguageSelector() {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (newLanguage: Language) => {
    setLanguage(newLanguage);
    setIsOpen(false);
  };

  // Find the current language option
  const currentOption = LANGUAGE_OPTIONS.find(opt => opt.value === language);
  const CurrentFlag = currentOption?.Flag;

  return (
    <div ref={dropdownRef} className="relative inline-block">
      {/* Current flag button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 p-2 rounded-md hover:bg-gray-100 transition-colors"
        aria-label="Select language"
      >
        {CurrentFlag && <CurrentFlag className="w-6 h-4" />}
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
        >
          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute top-full mt-1 bg-white rounded-md shadow-lg py-1 z-50">
          {LANGUAGE_OPTIONS.map(({ value, Flag }) => (
            <button
              key={value}
              onClick={() => handleLanguageChange(value as Language)}
              className={`w-full flex items-center px-4 py-2 hover:bg-gray-100 transition-colors
                ${value === language ? 'bg-gray-50' : ''}`}
              aria-label={`Switch to ${value === 'en' ? 'English' : 'German'}`}
            >
              <Flag className="w-6 h-4" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default LanguageSelector;
